import type BaseHttpService from "../../client/BaseHttpService";
import type { ApiResponse } from "../../client/types";
import { AbortError } from "../../client/types";
import { isCompleteIdentificationData, isCompletePriceData } from "./payload";
import type {
	AssessmentByIdentifierData,
	AssessmentsData,
	Brands,
	Client,
	CreateDiscrepancyData,
	CreateSessionData,
	DeviceType,
	DiscrepancyRecord,
	ExtractImeisData,
	FinalizeSessionData,
	GradingReport,
	IdentificationData,
	IdentificationParams,
	Image,
	Location,
	LockStatus,
	Model,
	Partner,
	PartnerTokenLoginResponse,
	PlatformAssessment,
	PriceData,
	Product,
	PromoEligibility,
	RecyclingEligibility,
	RedirectionData,
	Session,
	Transaction,
	TransactionListMetadata,
	VerifyPhoneNumberData,
	VoucherInfo,
} from "./types";

export * from "./types";

// Image uploads run over flaky in-store/mobile networks; give them a generous ceiling
// before a lost response is treated as a timeout (vs the default request timeout). See PND-65.
const IMAGE_UPLOAD_TIMEOUT_MS = 60_000;

export class PlatformApi {
	constructor(
		private readonly http: BaseHttpService,
		private readonly httpV2?: BaseHttpService,
		private readonly httpS3?: BaseHttpService,
	) {}

	// ─── Auth ───────────────────────────────────────────────────────────────

	async logInWithPartnerToken(partnerToken: string): Promise<PartnerTokenLoginResponse> {
		if (!this.httpV2) throw new Error("PlatformApi: httpV2 service is required for logInWithPartnerToken");
		const response = await this.httpV2.post<ApiResponse<PartnerTokenLoginResponse>>(
			"Logging in with partner token",
			"/auth/partner-token",
			{ token: partnerToken },
		);
		return response.data;
	}

	// ─── Partner & Location ─────────────────────────────────────────────────

	async getPartner(partnerCode: string): Promise<Partner> {
		const response = await this.http.get<ApiResponse<Partner>>(
			"Fetching partner info by code",
			`/partner/by-partner-code?code=${partnerCode}`,
		);
		return response.data;
	}

	async getAllPartners(partnersOnly = true): Promise<Partner[]> {
		const response = await this.http.get<ApiResponse<Partner[]>>(
			"Fetching all partners",
			`/partner/get-all?partnersOnly=${partnersOnly}`,
		);
		return response.data;
	}

	async getLocationAndPartner(locationId: string, options?: RequestInit): Promise<Location & { Partner: Partner }> {
		const response = await this.http.get<ApiResponse<Location & { Partner: Partner }>>(
			"Fetching location and partner info by location",
			`/partner/get-by-location?id=${locationId}`,
			undefined,
			options,
		);
		return response.data;
	}

	async getLocationsByPartner(
		partnerId: string,
		options?: { terminalType?: string; withRelated?: boolean },
	): Promise<Location[]> {
		const params = new URLSearchParams({ id: partnerId });
		if (options?.terminalType) params.set("terminalType", options.terminalType);
		if (options?.withRelated !== undefined) params.set("withRelated", String(options.withRelated));
		const response = await this.http.get<ApiResponse<Location[]>>(
			"Fetching locations by partner",
			`/location/get-by-partner?${params}`,
		);
		return response.data;
	}

	// ─── Session ────────────────────────────────────────────────────────────

	async createSession(data: CreateSessionData, options?: RequestInit): Promise<Session> {
		const response = await this.http.post<ApiResponse<Session>>(
			"Creating session",
			"/session/create",
			data,
			undefined,
			options,
		);
		return response.data;
	}

	async startSession(sessionId: string, options?: RequestInit): Promise<void> {
		await this.http.put("Starting session", `/session/${sessionId}/start`, undefined, undefined, options);
	}

	async continueEvaluation(identifier: string, partnerId: string): Promise<Session> {
		const response = await this.http.post<ApiResponse<Session>>(
			"Continuing evaluation",
			"/session/continue-evaluation",
			{ identifier, partnerId },
		);
		return response.data;
	}

	async updateSession(sessionId: string, locale: string): Promise<Session> {
		const response = await this.http.put<ApiResponse<Session>>(
			"Updating session locale",
			`/session/${sessionId}/update`,
			{ locale },
		);
		return response.data;
	}

	async getSessionById<T = Session>(sessionId: string, options?: RequestInit): Promise<T> {
		const response = await this.http.get<ApiResponse<T>>(
			"Fetching session by ID",
			`/session/${sessionId}`,
			undefined,
			options,
		);
		return response.data;
	}

	async addUserRating(sessionId: string, userRating: number, options?: RequestInit): Promise<void> {
		await this.http.put(
			"Adding user rating to session",
			`/session/${sessionId}/add-user-rating`,
			{ userRating },
			undefined,
			options,
		);
	}

	async verifyPhoneNumber(sessionId: string, data: VerifyPhoneNumberData, options?: RequestInit): Promise<void> {
		await this.http.post(
			"Verifying phone number",
			`/session/${sessionId}/verify-phonenumber`,
			data,
			undefined,
			options,
		);
	}

	async submitOtp(sessionId: string, otp: string, options?: RequestInit): Promise<void> {
		await this.http.post("Submitting OTP", `/session/${sessionId}/submit-otp`, { otp }, undefined, options);
	}

	async addTransient(sessionId: string, data: { key: string; value: string; ttl: number }): Promise<unknown[]> {
		const response = await this.http.post<ApiResponse<unknown[]>>(
			"Adding transient value to session",
			`/session/${sessionId}/add-transient`,
			data,
		);
		return response.data;
	}

	async getTransient<T = unknown>(sessionId: string, key: string, options?: RequestInit): Promise<T> {
		const response = await this.http.get<ApiResponse<T>>(
			"Fetching transient value from session",
			`/session/${sessionId}/get-transient?key=${key}`,
			undefined,
			options,
		);
		return response.data;
	}

	async getLockStatus(sessionId: string, type: DeviceType): Promise<LockStatus> {
		const response = await this.http.put<ApiResponse<LockStatus>>(
			"Fetching identification lock status",
			`/session/${sessionId}/identification/lock-status`,
			{ type },
		);
		return response.data;
	}

	async sendOfferEmail(
		sessionId: string,
		email: string | undefined,
		isFinalOffer: boolean,
		options?: RequestInit,
	): Promise<unknown[]> {
		const response = await this.http.post<ApiResponse<unknown[]>>(
			"Sending offer email",
			`/session/${sessionId}/send-offer-email`,
			{ email, isFinalOffer },
			undefined,
			options,
		);
		return response.data;
	}

	async finalizeSession(sessionId: string, data: FinalizeSessionData, options?: RequestInit): Promise<void> {
		await this.http.put("Finalizing session", `/session/${sessionId}/finalise`, data, undefined, options);
	}

	// ─── Identification ─────────────────────────────────────────────────────

	async getIdentification<T = IdentificationData>(
		data: IdentificationParams,
		options?: RequestInit,
	): Promise<T | null> {
		const params = new URLSearchParams(data as Record<string, string>);
		const response = await this.http.get<ApiResponse<T>>(
			"Fetching device info by identifier",
			`/identification/by-device-identifier?${params}`,
			undefined,
			options,
		);
		return isCompleteIdentificationData(response.data) ? response.data : null;
	}

	async updateCapacity(identificationId: string, capacity: string, options?: RequestInit): Promise<void> {
		await this.http.put<ApiResponse<unknown>>(
			"Updating device capacity",
			`/identification/${identificationId}/update-capacity`,
			{ capacity },
			undefined,
			options,
		);
	}

	async extractImeis(images: string[], options?: RequestInit): Promise<string[]> {
		const response = await this.http.post<ApiResponse<ExtractImeisData>>(
			"Extracting IMEIs from images",
			"/extract-imei",
			{ images },
			undefined,
			options,
		);
		return response.data.imeis;
	}

	async getIdentificationByProductId(
		params: {
			productId: string;
			sessionId: string;
			identifier?: string;
			identifierType?: string;
			isManualIdentification?: boolean;
		},
		options?: RequestInit,
	): Promise<unknown> {
		const defined = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<
			string,
			string
		>;
		const response = await this.http.get<ApiResponse<unknown>>(
			"Fetching identification by product ID",
			`/identification/by-product-id?${new URLSearchParams(defined)}`,
			undefined,
			options,
		);
		return response.data;
	}

	// ─── Assessments ────────────────────────────────────────────────────────

	async getAssessmentsByIdentifier(identifier: string, partnerId: string): Promise<AssessmentByIdentifierData> {
		const params = new URLSearchParams({ identifier, partnerId, onlyFunctional: "yes" });
		const response = await this.http.get<ApiResponse<AssessmentByIdentifierData>>(
			"Fetching assessments by identifier",
			`/assessment/get-assessments-by-identifier?${params}`,
		);
		return response.data;
	}

	async getSupportedAssessments<T = AssessmentsData>(
		version: string,
		deviceType: string,
		actors?: Client[],
		options?: RequestInit,
	): Promise<T> {
		const deviceTypeLower = deviceType.toLowerCase();
		const resolvedActors = actors ?? (deviceTypeLower === "smartwatch" ? ["pricing"] : ["remote_grader"]);
		const actorParam = new URLSearchParams(resolvedActors.map((actor) => ["actor", actor]));
		const response = await this.http.get<ApiResponse<T>>(
			"Fetching supported assessment type components",
			`/assessment/get-assessment-type-components?${actorParam}&deviceType=${deviceTypeLower}&version=${version}`,
			undefined,
			options,
		);
		return response.data;
	}

	async submitAssessments(
		sessionId: string,
		assessments: PlatformAssessment[],
		options?: RequestInit,
	): Promise<ApiResponse<null>> {
		const response = await this.http.post<ApiResponse<null>>(
			"Submitting assessments",
			`/session/${sessionId}/add-assessments`,
			{ assessments },
			undefined,
			options,
		);
		return response;
	}

	async getGradingReport<T = GradingReport["assessments"]>(sessionId: string, options?: RequestInit): Promise<T> {
		const response = await this.http.get<ApiResponse<{ assessments: T }>>(
			"Fetching grading report",
			`/session/${sessionId}/get-grading-report`,
			undefined,
			options,
		);
		return response.data.assessments;
	}

	// ─── Pricing ────────────────────────────────────────────────────────────

	async getPrice<T = PriceData>(sessionId: string, options?: RequestInit): Promise<T | null> {
		const response = await this.http.get<ApiResponse<T>>(
			"Fetching session price",
			`/session/${sessionId}/get-price`,
			undefined,
			options,
		);
		return isCompletePriceData(response.data) ? response.data : null;
	}

	async getMaxPrice(sessionId: string): Promise<PriceData | null> {
		const response = await this.http.get<ApiResponse<PriceData>>(
			"Fetching session max price",
			`/session/${sessionId}/get-max-price`,
		);
		return isCompletePriceData(response.data) ? response.data : null;
	}

	async checkPromoEligibility(sessionId: string, code: string, validationKey: string): Promise<PromoEligibility> {
		const response = await this.http.get<ApiResponse<PromoEligibility>>(
			"Checking promo eligibility",
			`/session/${sessionId}/promo/eligibility?code=${code}&validationKey=${validationKey}`,
		);
		return response.data;
	}

	async getRedirectionUrl(sessionId: string, partnerId: string): Promise<string> {
		const response = await this.http.get<ApiResponse<RedirectionData>>(
			"Fetching redirection URL",
			`/webapp/redirection?sessionId=${sessionId}&partnerId=${partnerId}`,
		);
		return response.data.redirectionURL;
	}

	// ─── Products ───────────────────────────────────────────────────────────

	async getBrands(partnerCode: string): Promise<{ brands: Brands }> {
		const response = await this.http.get<ApiResponse<{ brands: Brands }>>(
			"Fetching brands",
			`/products/get-brands?partnerCode=${partnerCode.toLowerCase()}`,
		);
		return response.data;
	}

	async getBrandsByDeviceType(partnerCode: string, deviceType: DeviceType): Promise<string[] | undefined> {
		const result = await this.getBrands(partnerCode.toLowerCase());
		return result.brands[deviceType];
	}

	async getProductsByBrandName(
		brandName: string,
		deviceType: string,
		partnerCode: string,
	): Promise<{ products: Model[] }> {
		const params = new URLSearchParams({
			brand: brandName,
			deviceType,
			partnerCode: partnerCode.toLowerCase(),
		});
		const response = await this.http.get<ApiResponse<{ products: Model[] }>>(
			"Fetching products by brand name",
			`/products/get-products-by-brand-name?${params}`,
		);
		return response.data;
	}

	async getModelVariations(productId: string, partnerCode: string): Promise<{ products: Product[] }> {
		const params = new URLSearchParams({ productId, partnerCode: partnerCode.toLowerCase() });
		const response = await this.http.get<ApiResponse<{ products: Product[] }>>(
			"Fetching model variations",
			`/products/get-model-variations?${params}`,
		);
		return response.data;
	}

	// ─── Vouchers ───────────────────────────────────────────────────────────

	async checkVoucher(voucherCode: string, partnerId: string): Promise<VoucherInfo> {
		const response = await this.http.get<ApiResponse<VoucherInfo>>(
			"Checking voucher",
			`/partner/${partnerId}/check-voucher?code=${voucherCode}`,
		);
		return response.data;
	}

	async redeemVoucher(voucherCode: string, partnerId: string, locationId: string): Promise<void> {
		await this.http.put<ApiResponse<unknown>>("Redeeming voucher", `/partner/${partnerId}/redeem-voucher`, {
			code: voucherCode,
			locationId,
		});
	}

	async cancelVoucher(voucherCode: string, partnerId: string, locationId: string): Promise<void> {
		await this.http.put<ApiResponse<unknown>>("Cancelling voucher", `/partner/${partnerId}/cancel-voucher`, {
			code: voucherCode,
			locationId,
		});
	}

	// ─── Transactions ────────────────────────────────────────────────────────

	async getTransactions(
		locationId: string,
		currentPage = "1",
		perPage = "10",
		startDate?: string,
		endDate?: string,
	): Promise<{ transactions: Transaction[]; metadata?: TransactionListMetadata }> {
		let path = `/location/${locationId}/transactions?currentPage=${currentPage}&perPage=${perPage}`;
		if (startDate && endDate) path += `&startDate=${startDate}&endDate=${endDate}`;
		const response = await this.http.get<{
			success: boolean;
			data: Transaction[];
			message: string;
			metadata?: TransactionListMetadata;
		}>("Fetching transactions", path);
		return { transactions: response.data, metadata: response.metadata };
	}

	async getTransactionExportData(
		locationId: string,
		startDate: string,
		endDate: string,
	): Promise<{ blob: Blob; filename: string }> {
		const response = await fetch(`${this.http.baseUrl}/location/${locationId}/transactions/export`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ startDate, endDate }),
		});
		if (!response.ok) throw new Error(`Transaction export failed: ${response.statusText}`);
		const contentDisposition = response.headers.get("content-disposition");
		const filenameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
		const filename = filenameMatch?.[1] ? decodeURIComponent(filenameMatch[1]) : "transactions.csv";
		return { blob: await response.blob(), filename };
	}

	// ─── Recycling ───────────────────────────────────────────────────────────

	async isEligibleForRecycling(identifier: string, partnerId: string): Promise<RecyclingEligibility> {
		const response = await this.http.get<ApiResponse<RecyclingEligibility>>(
			"Checking recycling eligibility",
			`/recycling/eligibility?identifier=${identifier}&partnerId=${partnerId}`,
		);
		return response.data;
	}

	async completeRecycling(
		identifier: string,
		locationId: string,
		client: Client,
		locale: string,
		fullName: string,
		email: string,
		phoneNumber: string,
		sessionId?: string,
	): Promise<ApiResponse<null>> {
		const response = await this.http.post<ApiResponse<null>>("Completing recycling", "/recycling/complete", {
			identifier,
			locationId,
			client,
			locale,
			sessionId,
			fullName,
			email,
			phoneNumber,
		});
		return response;
	}

	// ─── Transaction discrepancy ─────────────────────────────────────────────

	async createDiscrepancyRecord(data: CreateDiscrepancyData, options?: RequestInit): Promise<DiscrepancyRecord> {
		const response = await this.http.post<ApiResponse<DiscrepancyRecord>>(
			"Creating transaction discrepancy record",
			"/transaction-discrepancy/create",
			data,
			undefined,
			options,
		);
		return response.data;
	}

	async submitDiscrepancyAssessments(
		discrepancyId: string,
		assessments: PlatformAssessment[],
		options?: RequestInit,
	): Promise<ApiResponse<null>> {
		const response = await this.http.post<ApiResponse<null>>(
			"Submitting discrepancy assessments",
			`/transaction-discrepancy/${discrepancyId}/add-assessments`,
			{ assessments },
			undefined,
			options,
		);
		return response;
	}

	async getDiscrepancyPrice<T = PriceData>(discrepancyId: string, options?: RequestInit): Promise<T | null> {
		const response = await this.http.get<ApiResponse<T>>(
			"Fetching discrepancy price",
			`/transaction-discrepancy/${discrepancyId}/get-price`,
			undefined,
			options,
		);
		return isCompletePriceData(response.data) ? response.data : null;
	}

	async getDiscrepancyGradingReport<T = GradingReport["assessments"]>(
		discrepancyId: string,
		options?: RequestInit,
	): Promise<T> {
		const response = await this.http.get<ApiResponse<{ assessments: T }>>(
			"Fetching discrepancy grading report",
			`/transaction-discrepancy/${discrepancyId}/get-grading-report`,
			undefined,
			options,
		);
		return response.data.assessments;
	}

	async addDiscrepancyMedia(discrepancyId: string, links: string[], options?: RequestInit): Promise<void> {
		await this.http.post<ApiResponse<string[]>>(
			"Adding discrepancy media",
			`/transaction-discrepancy/${discrepancyId}/add-media`,
			{ links },
			undefined,
			options,
		);
	}

	async generateDiscrepancyPresignedUrls(
		discrepancyId: string,
		fileNames: string[],
		options?: RequestInit,
	): Promise<string[]> {
		const response = await this.http.post<ApiResponse<string[]>>(
			"Generating discrepancy presigned URLs",
			`/transaction-discrepancy/${discrepancyId}/generate-presigned-url`,
			{ files: fileNames },
			undefined,
			options,
		);
		return response.data;
	}

	// ─── Media upload ────────────────────────────────────────────────────────

	async generatePresignedUrls(sessionId: string, fileNames: string[], options?: RequestInit): Promise<string[]> {
		const response = await this.http.post<ApiResponse<string[]>>(
			"Generating presigned URLs",
			`/session/${sessionId}/generate-presigned-url`,
			{ files: fileNames },
			undefined,
			options,
		);
		return response.data;
	}

	async addSessionMedia(sessionId: string, links: string[], options?: RequestInit): Promise<void> {
		await this.http.post<ApiResponse<string[]>>(
			"Adding session media",
			`/session/${sessionId}/add-session-media`,
			{ links },
			undefined,
			options,
		);
	}

	async uploadMedia(sessionId: string, images: Image[], signal?: AbortSignal | null): Promise<string[]> {
		const fileNames: string[] = [];
		const files: Blob[] = [];
		const uploadedLinks: string[] = [];

		if (signal?.aborted) throw new AbortError("Upload aborted");

		for (const { photo, photoName } of images) {
			const now = new Date();
			const pad = (n: number) => String(n).padStart(2, "0");
			const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
			fileNames.push(`${photoName}-${dateStr}.jpeg`);
			if (photo.size === 0) throw new Error(`Blob for ${photoName} is empty.`);
			files.push(photo);
		}

		const presignedUrls = await this.generatePresignedUrls(sessionId, fileNames, { signal: signal ?? undefined });

		for (let i = 0; i < presignedUrls.length; i++) {
			if (signal?.aborted) throw new AbortError("Upload aborted");
			const presignedUrl = presignedUrls[i]!;

			if (this.httpS3) {
				await this.httpS3.put(
					"Uploading file to presigned URL",
					presignedUrl,
					files[i],
					{ "Content-Type": "image/jpeg" },
					{ signal: signal ?? undefined, timeoutMs: IMAGE_UPLOAD_TIMEOUT_MS },
				);
			} else {
				// No shared httpS3 client (e.g. webapp/pricing): guard the raw PUT with the same
				// upload timeout so a lost response can't hang the capture flow forever (PND-65).
				const timeoutSignal = AbortSignal.timeout(IMAGE_UPLOAD_TIMEOUT_MS);
				const putSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
				const response = await fetch(presignedUrl, {
					method: "PUT",
					body: files[i],
					headers: { "Content-Type": "image/jpeg" },
					signal: putSignal,
				});
				if (!response.ok) throw new Error(`Failed to upload file to presigned URL: ${response.statusText}`);
			}

			uploadedLinks.push(`${presignedUrl.split(".amazonaws.com")[0]}.amazonaws.com/${sessionId}/${fileNames[i]}`);
		}

		return uploadedLinks;
	}
}
